import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_FOHM_KUNSKAPSPROV, SOURCE_FOHM_KUNSKAPSPROV_FAFS, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vad innehåller kunskapsprovet?", a: "Provet täcker alkohollagen, serveringsansvar, tillsyn, ålderskontroll, ordning, marknadsföring och fler områden enligt Folkhälsomyndighetens riktlinjer." },
  { q: "Hur många frågor är det?", a: "Frågeantalet varierar beroende på tillståndstyp och kommun. Folkhälsomyndighetens föreskrifter FoHMFS 2014:7 anger områden och miniminivåer; godkäntgränsen är minst 75 % rätt per delprov." },
  { q: "Hur många gånger får jag göra om provet?", a: "Du får normalt göra om provet flera gånger, men varje provtillfälle kan vara avgiftsbelagt." },
];

export const Route = createFileRoute("/kunskapsprov-alkohollagen")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Kunskapsprov" },
      ]}
      title="Kunskapsprov alkohollagen 2026"
      intro="Så fungerar kunskapsprovet för serveringstillstånd – innehåll, format och hur du bäst förbereder dig."
      lastReviewed={LAST_REVIEWED}
      sources={[SOURCE_FOHM_KUNSKAPSPROV, SOURCE_FOHM_KUNSKAPSPROV_FAFS, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Vad är kunskapsprovet?", body: "Kunskapsprovet är ett obligatoriskt prov för dig som söker serveringstillstånd. Det säkerställer att tillståndshavare har tillräcklig kunskap i alkohollagen och dess tillämpning." },
        { h2: "Provets innehåll", body: "Frågorna behandlar alkohollagen, serveringsansvar, tillsyn, ålderskontroll, ordning och nykterhet, marknadsföring, serveringstider, egenkontroll, catering, folköl och mer." },
        { h2: "Så förbereder du dig", body: "Träna systematiskt på alla områden i Alkoholprovet. Använd provsimuleringen för att vänja dig vid formatet, och kolla din statistik för att hitta dina svaga punkter." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/ovningsprov-serveringstillstand", label: "Övningsprov" },
          { to: "/alkohollagen-fragor", label: "Frågor om alkohollagen" },
          { to: "/serveringstillstand", label: "Serveringstillstånd – översikt" },
          { to: "/serveringstillstand-stockholm", label: "Kunskapsprov Stockholm" },
          { to: "/serveringstillstand-goteborg", label: "Kunskapsprov Göteborg" },
          { to: "/serveringstillstand-malmo", label: "Kunskapsprov Malmö" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Kunskapsprov alkohollagen 2026 – så funkar provet" },
      { name: "description", content: "Lär dig allt om kunskapsprovet i alkohollagen. Innehåll, format och hur du förbereder dig inför 2026." },
      { property: "og:title", content: "Kunskapsprov alkohollagen 2026 – så funkar provet" },
      { property: "og:description", content: "Allt du behöver veta om kunskapsprovet i alkohollagen: innehåll, format och förberedelser inför 2026." },
      { property: "og:url", content: "https://alkoholprovet.se/kunskapsprov-alkohollagen" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/kunskapsprov-alkohollagen" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Kunskapsprov", url: "https://alkoholprovet.se/kunskapsprov-alkohollagen" },
      ]),
      articleJsonLd({
        url: "https://alkoholprovet.se/kunskapsprov-alkohollagen",
        headline: "Kunskapsprov alkohollagen 2026 – så funkar provet",
        description: "Lär dig allt om kunskapsprovet i alkohollagen. Innehåll, format och hur du förbereder dig inför 2026.",
        datePublished: "2026-01-15",
        dateModified: "2026-06-10",
      }),
    ],
  }),
});
